import { zodResolver } from "@hookform/resolvers/zod";
import type { ColumnDef } from "@tanstack/react-table";
import { AlertCircle, ChevronDown, ChevronUp, MoreVertical, Plus, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { DataTable } from "@/components/admin/DataTable";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  useAdminPeople,
  useCreatePerson,
  useDeletePerson,
  useMovePerson,
  useUpdatePerson,
  useUploadPersonPhoto,
} from "@/hooks/admin/people";
import { AdminLayout } from "@/layouts/AdminLayout";
import { cn } from "@/lib/utils";
import type { Person, PersonGroup } from "@/types";

const GROUPS: { value: PersonGroup; label: string }[] = [
  { value: "leadership", label: "Leadership" },
  { value: "advisors", label: "Advisors" },
];

const personFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  job_title: z.string().trim().min(1, "Job title is required"),
  credentials: z.string().trim(),
  bio: z.string().trim().min(1, "The one-line bio is required"),
  profile: z.string().trim(),
  chips: z.string().trim(),
  group: z.enum(["leadership", "advisors"]),
  linkedin_url: z
    .string()
    .trim()
    .refine((v) => v === "" || /^https?:\/\/.+/i.test(v), "Must be a valid http(s) URL"),
  is_published: z.boolean(),
});
type PersonFormValues = z.infer<typeof personFormSchema>;

function toFormValues(p: Person): PersonFormValues {
  return {
    name: p.name,
    job_title: p.job_title,
    credentials: p.credentials ?? "",
    bio: p.bio,
    profile: p.profile ?? "",
    chips: p.chips.join(", "),
    group: p.group,
    linkedin_url: p.linkedin_url ?? "",
    is_published: p.is_published,
  };
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function PersonAvatar({ person, size = "h-10 w-10" }: { person: Person; size?: string }) {
  if (person.has_uploaded_photo && person.photo_url) {
    return (
      <img
        src={person.photo_url}
        alt={person.name}
        className={cn(size, "flex-shrink-0 rounded-full object-cover")}
      />
    );
  }
  // Legacy photos live in the website repo, so they can't be previewed here.
  return (
    <div
      className={cn(
        size,
        "flex flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-500"
      )}
      title={person.photo_url ? "Photo served from the website (legacy)" : undefined}
    >
      {initials(person.name)}
    </div>
  );
}

function PhotoSection({ person }: { person: Person }) {
  const uploadPhoto = useUploadPersonPhoto();
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      await uploadPhoto.mutateAsync({ id: person.id, file });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload photo");
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-4">
        <PersonAvatar person={person} size="h-14 w-14" />
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-900">Photo</div>
          <p className="mt-0.5 text-xs text-gray-500">
            {person.has_uploaded_photo
              ? "Uploaded photo shown on the website."
              : person.photo_url
                ? "Currently served from the website's static files — upload to replace it."
                : "No photo yet — initials are shown instead."}
          </p>
          <Input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={uploadPhoto.isPending}
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="mt-2 h-auto cursor-pointer py-1.5 text-xs"
          />
          {uploadPhoto.isPending && <p className="mt-1 text-xs text-gray-500">Uploading…</p>}
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
      </div>
    </div>
  );
}

function PersonFormModal({
  person,
  defaultGroup,
  onClose,
}: {
  person?: Person;
  defaultGroup: PersonGroup;
  onClose: () => void;
}) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const createPerson = useCreatePerson();
  const updatePerson = useUpdatePerson();
  const form = useForm<PersonFormValues>({
    resolver: zodResolver(personFormSchema),
    defaultValues: person
      ? toFormValues(person)
      : {
          name: "",
          job_title: "",
          credentials: "",
          bio: "",
          profile: "",
          chips: "",
          group: defaultGroup,
          linkedin_url: "",
          is_published: true,
        },
  });
  const { control } = form;
  const isSubmitting = form.formState.isSubmitting;

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    const nn = (v: string) => (v.trim() ? v.trim() : null);
    const payload = {
      name: values.name.trim(),
      job_title: values.job_title.trim(),
      credentials: nn(values.credentials),
      bio: values.bio.trim(),
      profile: nn(values.profile),
      chips: values.chips
        .split(",")
        .map((chip) => chip.trim())
        .filter(Boolean),
      group: values.group,
      linkedin_url: nn(values.linkedin_url),
      is_published: values.is_published,
    };
    try {
      if (person) {
        await updatePerson.mutateAsync({ id: person.id, data: payload });
      } else {
        await createPerson.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save person");
    }
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white text-gray-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 text-xl">
            {person ? "Edit Person" : "Add Person"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <Form {...form}>
          <form onSubmit={onSubmit} className="max-h-[calc(90vh-80px)] overflow-y-auto p-6">
            {submitError && (
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{submitError}</span>
              </div>
            )}
            <div className="space-y-4">
              {person && <PhotoSection person={person} />}
              <FormField
                control={control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Name<span className="text-red-500"> *</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="job_title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Job title<span className="text-red-500"> *</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="group"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Group</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {GROUPS.map((g) => (
                          <SelectItem key={g.value} value={g.value}>
                            {g.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="credentials"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Credential line</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. CFA · CAIA" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="bio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      One-line bio<span className="text-red-500"> *</span>
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={2} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="profile"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Long profile</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={8} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="chips"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Chips (comma-separated)</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. ex-Bloomberg, Multi-asset" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="linkedin_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LinkedIn URL</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="https://www.linkedin.com/in/…" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="is_published"
                render={({ field }) => (
                  <FormItem>
                    <label className="flex cursor-pointer items-center gap-2">
                      <Checkbox
                        checked={Boolean(field.value)}
                        onCheckedChange={(c) => field.onChange(c === true)}
                      />
                      <span className="text-sm text-gray-700">
                        Published — {field.value ? "visible on the site" : "hidden from the site"}
                      </span>
                    </label>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-lg bg-primary px-4 py-2 font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {isSubmitting ? "Saving…" : person ? "Save Changes" : "Add Person"}
              </button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

function DeletePersonModal({ person, onClose }: { person: Person | null; onClose: () => void }) {
  const deletePerson = useDeletePerson();
  const [error, setError] = useState<string | null>(null);

  if (!person) return null;

  const handleDelete = async () => {
    setError(null);
    try {
      await deletePerson.mutateAsync(person.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete person");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white text-gray-900 rounded-2xl w-full max-w-md shadow-2xl p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Delete Person</h2>
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete <strong>{person.name}</strong>? This cannot be undone.
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deletePerson.isPending}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg transition-colors font-medium"
          >
            {deletePerson.isPending ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminPeoplePage() {
  const [group, setGroup] = useState<PersonGroup>("leadership");
  const [personModal, setPersonModal] = useState<{ open: boolean; person?: Person }>({
    open: false,
  });
  const [deletingPerson, setDeletingPerson] = useState<Person | null>(null);

  const peopleQuery = useAdminPeople(group);
  const people = peopleQuery.data ?? [];
  const error = peopleQuery.isError
    ? peopleQuery.error instanceof Error
      ? peopleQuery.error.message
      : "Failed to load people"
    : null;

  const movePerson = useMovePerson();
  const { mutate: mutateUpdatePerson } = useUpdatePerson();

  const columns = useMemo<ColumnDef<Person>[]>(
    () => [
      {
        id: "order",
        header: "",
        cell: ({ row }) => {
          const index = row.index;
          return (
            <div className="flex flex-col">
              <button
                type="button"
                aria-label={`Move ${row.original.name} up`}
                disabled={index === 0 || movePerson.isPending}
                onClick={() => movePerson.mutate({ id: row.original.id, direction: "up" })}
                className="rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:pointer-events-none disabled:opacity-30"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label={`Move ${row.original.name} down`}
                disabled={index === people.length - 1 || movePerson.isPending}
                onClick={() => movePerson.mutate({ id: row.original.id, direction: "down" })}
                className="rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:pointer-events-none disabled:opacity-30"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          );
        },
      },
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <PersonAvatar person={row.original} />
            <div>
              <div className="font-medium text-gray-900">{row.original.name}</div>
              <div className="text-xs text-gray-500">{row.original.job_title}</div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: "credentials",
        header: "Credentials",
        cell: ({ row }) => (
          <span className="text-sm text-gray-600">{row.original.credentials ?? "—"}</span>
        ),
      },
      {
        id: "profile",
        header: "Long profile",
        cell: ({ row }) => (
          <span className="text-sm text-gray-600">{row.original.profile ? "Yes" : "—"}</span>
        ),
      },
      {
        accessorKey: "is_published",
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge tone={row.original.is_published ? "success" : "neutral"}>
            {row.original.is_published ? "Published" : "Hidden"}
          </StatusBadge>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const person = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Person actions"
                  className="rounded-lg p-1.5 transition-colors hover:bg-gray-100"
                >
                  <MoreVertical className="h-4 w-4 text-gray-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setPersonModal({ open: true, person })}>
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    mutateUpdatePerson({
                      id: person.id,
                      data: { is_published: !person.is_published },
                    })
                  }
                >
                  {person.is_published ? "Hide" : "Publish"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDeletingPerson(person)}
                  className="text-red-600 focus:text-red-600"
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [people.length, movePerson, mutateUpdatePerson]
  );

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        <DataTable
          className="h-full"
          columns={columns}
          data={people}
          isLoading={peopleQuery.isPending}
          error={error}
          onRetry={() => peopleQuery.refetch()}
          emptyMessage="No people in this group yet"
          getRowId={(p) => p.id}
          toolbar={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                {GROUPS.map((g) => (
                  <button
                    key={g.value}
                    type="button"
                    onClick={() => setGroup(g.value)}
                    className={cn(
                      "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                      group === g.value
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-900"
                    )}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setPersonModal({ open: true })}
                className="flex flex-shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" />
                Add Person
              </button>
            </div>
          }
        />
      </div>

      {personModal.open && (
        <PersonFormModal
          person={personModal.person}
          defaultGroup={group}
          onClose={() => setPersonModal({ open: false })}
        />
      )}

      <DeletePersonModal person={deletingPerson} onClose={() => setDeletingPerson(null)} />
    </AdminLayout>
  );
}
